import { motion } from 'framer-motion';
import { ExternalLink, Plane, ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { ROUTE_PATHS } from '@/lib/index';
import FlightSearch from '@/components/FlightSearch';
import { springPresets } from '@/lib/motion';

export default function Booking() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative h-64 overflow-hidden">
        <img src="/images/dreamliner.jpg" alt="航班预订" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 to-secondary/80" />
        <div className="absolute inset-0 flex items-center pt-20">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">航班查询与预订</h1>
            <p className="text-white/70">广州直飞达累斯萨拉姆 · 桑给巴尔 · 东非各地</p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold mb-6 text-center">搜索航班</h2>
          <FlightSearch />

          <div className="mt-8 p-5 bg-primary/5 border border-primary/20 rounded-2xl">
            <p className="text-sm text-muted-foreground text-center">
              ✈️ 搜索将跳转至坦桑尼亚航空官网预订系统，支持支付宝/微信支付（官网首页）及信用卡付款
            </p>
          </div>
        </div>
      </section>

      {/* Direct Book CTA */}
      <section className="py-12 bg-muted">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8">预订渠道</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: '🌐',
                title: '官方网站预订',
                desc: '直连坦桑尼亚航空官方订票系统，支持在线支付',
                cta: '前往官网',
                href: 'https://www.airtanzania.co.tz',
                primary: true,
              },
              {
                icon: '📞',
                title: '电话预订',
                desc: '拨打广州代表处电话，由中文客服人员协助办理',
                cta: '020-38550084',
                href: 'tel:020-38550084',
                primary: false,
              },
              {
                icon: '✉️',
                title: '代理人预订',
                desc: '通过IATA认可的旅行社或BSP代理预订，享受专属服务',
                cta: '发送邮件',
                href: 'mailto:canto@airtanzania.cn',
                primary: false,
              },
            ].map((ch) => (
              <motion.a
                key={ch.title}
                href={ch.href}
                target={ch.href.startsWith('http') ? '_blank' : undefined}
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={springPresets.gentle}
                className={`flex flex-col p-6 rounded-2xl border transition-all hover:shadow-lg ${
                  ch.primary
                    ? 'bg-secondary text-white border-secondary'
                    : 'bg-card border-border hover:border-primary/30'
                }`}
              >
                <div className="text-3xl mb-3">{ch.icon}</div>
                <h3 className={`font-bold mb-2 ${ch.primary ? 'text-white' : ''}`}>{ch.title}</h3>
                <p className={`text-sm mb-4 flex-1 ${ch.primary ? 'text-white/70' : 'text-muted-foreground'}`}>{ch.desc}</p>
                <div className={`flex items-center gap-2 text-sm font-semibold ${ch.primary ? 'text-accent' : 'text-primary'}`}>
                  {ch.primary && <ExternalLink size={14} />}
                  {ch.cta}
                  <ArrowRight size={14} />
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Cabin Classes */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-2">选择您的舱位</h2>
          <p className="text-center text-muted-foreground mb-10">波音787-8梦想客机提供两种精心设计的客舱体验</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Business */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="rounded-2xl overflow-hidden border border-border"
            >
              <div className="relative h-52">
                <img src="/images/twiga-lounge.jpg" alt="公务舱" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 bg-accent text-accent-foreground text-xs px-3 py-1.5 rounded-full font-semibold">
                  ✨ 公务舱
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold">公务舱</h3>
                  <div className="flex gap-0.5 text-accent">
                    {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
                  </div>
                </div>
                <p className="text-muted-foreground text-sm mb-4">顶级商务旅行体验，180度平躺床铺，私密空间，优先值机</p>
                <ul className="space-y-2 mb-5">
                  {['180度可全平躺床铺', '优先登机 & 值机', '3件×23kg行李额度', '免费机场贵宾室', 'Twiga Lounge专属', '精选葡萄酒与美食'].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <span className="text-accent font-bold">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to={ROUTE_PATHS.BOOKING} className="block text-center py-3 bg-accent text-accent-foreground rounded-xl font-semibold hover:bg-accent/90 transition-colors">
                  预订公务舱
                </Link>
              </div>
            </motion.div>

            {/* Economy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springPresets.gentle, delay: 0.1 }}
              className="rounded-2xl overflow-hidden border border-border"
            >
              <div className="relative h-52">
                <img src="/images/experience.jpg" alt="经济舱" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs px-3 py-1.5 rounded-full font-semibold">
                  经济舱
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold">经济舱</h3>
                  <div className="flex gap-0.5 text-primary">
                    {[...Array(4)].map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
                    <Star size={12} className="text-muted-foreground" />
                  </div>
                </div>
                <p className="text-muted-foreground text-sm mb-4">宽敞舒适的经济舱，享受787特有的大舷窗与高湿度客舱</p>
                <ul className="space-y-2 mb-5">
                  {['2件×23kg行李额度', '全程娱乐系统', '宽大舷窗（787特有）', '优质机上餐食', '毯子与枕头', 'Twiga Miles积分'].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <span className="text-primary font-bold">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to={ROUTE_PATHS.BOOKING} className="block text-center py-3 bg-secondary text-white rounded-xl font-semibold hover:bg-secondary/90 transition-colors">
                  预订经济舱
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Baggage */}
      <section className="py-12 bg-muted">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-xl font-bold text-center mb-2">行李政策（广州直飞航线）</h2>
          <p className="text-center text-muted-foreground text-sm mb-8">以下为 Air Tanzania 广州直飞达累斯萨拉姆/桑给巴尔航线行李规定</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* 公务舱 */}
            <div className="bg-secondary text-white rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-bold bg-accent text-accent-foreground px-2.5 py-1 rounded-full">公务舱</span>
                <span className="text-white/50 text-xs">Business Class</span>
              </div>
              <div className="mb-5">
                <p className="text-white/50 text-xs uppercase tracking-wider mb-2">🧳 托运行李</p>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-3xl">🧳🧳🧳</span>
                  <div>
                    <div className="text-white font-black text-lg">3件 × 每件 23 kg</div>
                    <div className="text-white/50 text-xs">每件尺寸 L+W+H ≤ 158 cm</div>
                  </div>
                </div>
              </div>
              <div className="border-t border-white/10 pt-4">
                <p className="text-white/50 text-xs uppercase tracking-wider mb-2">💼 随身手提</p>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">💼</span>
                  <div>
                    <div className="text-white font-black text-lg">10 kg</div>
                    <div className="text-white/50 text-xs">L+W+H ≤ 115 cm</div>
                  </div>
                </div>
              </div>
            </div>
            {/* 经济舱 */}
            <div className="bg-card border border-border rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-bold bg-primary/10 text-primary px-2.5 py-1 rounded-full border border-primary/20">经济舱</span>
                <span className="text-muted-foreground text-xs">Economy Class</span>
              </div>
              <div className="mb-5">
                <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">🧳 托运行李</p>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-3xl">🧳🧳</span>
                  <div>
                    <div className="font-black text-lg">2件 × 每件 23 kg</div>
                    <div className="text-muted-foreground text-xs">每件尺寸 L+W+H ≤ 158 cm</div>
                  </div>
                </div>
              </div>
              <div className="border-t border-border pt-4">
                <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">💼 随身手提</p>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">💼</span>
                  <div>
                    <div className="font-black text-lg">7 kg</div>
                    <div className="text-muted-foreground text-xs">L+W+H ≤ 115 cm</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            * 超出规定行李额度需付超重费。婴儿（2岁以下）额外允许1件折叠婴儿车。体育器材请提前联系代表处确认。
          </p>
        </div>
      </section>
    </Layout>
  );
}
